import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useMemo, useRef, useState } from "react"
import { DateRangeFilter } from "../components/DateRangeFilter"
import {
  CourseFilterSelect,
  CourseOption,
} from "../components/CourseFilterSelect"
import {
  GroupFilterSelect,
  useGroupFilter,
} from "../components/GroupFilterSelect"
import { ReportPageHeader } from "../components/ReportPageHeader"
import { userCourseReportPage } from "../constants/reportPages"
import {
  exportUserCoursesAsCSV,
  getPaginationMeta,
  GetUserCourseParams,
  useGetUserCoursesQuery,
  UserCourseSummary,
} from "../services/api"
import { useAppSelector } from "../store/hooks"
import { selectActiveOrgId } from "../store/sessionSlice"
import { formatDateTime } from "./reportFormatters"

type Status = NonNullable<GetUserCourseParams["status"]>
const statusLabels: Record<Status, string> = {
  in_progress: "In Progress",
  grading_in_progress: "Grading in Progress",
  submitted: "Submitted",
  completed: "Completed",
  saved_for_later: "Saved for Later",
}
const dateRanges = [["completed", "Completion Date Range"]] as const
const emptyFilters = {
  status: "" as Status | "",
  completed_start: "",
  completed_end: "",
}
type Filters = typeof emptyFilters

const columns: GridColDef<UserCourseSummary>[] = [
  {
    field: "userName",
    headerName: "User Name",
    width: 200,
    sortable: false,
    valueGetter: (_value, row) =>
      [row.user?.first_name, row.user?.last_name].filter(Boolean).join(" ") ||
      "-",
  },
  {
    field: "userEmail",
    headerName: "Email",
    width: 240,
    sortable: false,
    valueGetter: (_value, row) => row.user?.email || "-",
  },
  {
    field: "courseTitle",
    headerName: "Course",
    width: 280,
    sortable: false,
    valueGetter: (_value, row) => row.course?.title || "-",
  },
  {
    field: "status",
    headerName: "Status",
    width: 180,
    sortable: false,
    renderCell: ({ row }) =>
      row.status ? (
        <Chip
          size="small"
          color={row.status === "completed" ? "success" : "info"}
          label={statusLabels[row.status] || row.status}
        />
      ) : (
        "-"
      ),
  },
  ...(["started", "completed", "updated"] as const).map((field) => ({
    field,
    headerName:
      field === "updated"
        ? "Last Updated"
        : field === "completed"
        ? "Completed"
        : "Started",
    width: 200,
    sortable: false,
    valueGetter: (_value: unknown, row: UserCourseSummary) =>
      formatDateTime(row[field]),
  })),
]

function UserCourseReport({ orgId }: { orgId: string }) {
  const { selectedGroups, setSelectedGroups, groupParams } =
    useGroupFilter(orgId)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [filters, setFilters] = useState<Filters>(emptyFilters)
  const [selectedCourses, setSelectedCourses] = useState<CourseOption[]>([])
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<GetUserCourseParams>
  >({})
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(false)
  const queryParams: GetUserCourseParams = {
    ...appliedFilters,
    ...groupParams,
    org_id: orgId,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
  }
  // currentData prevents the previous organization's/filter's rows appearing
  // while the new request is in flight.
  const {
    currentData: data,
    error,
    isFetching,
  } = useGetUserCoursesQuery(queryParams)
  const total = getPaginationMeta(data)?.total
  const rowCountRef = useRef(0)
  const rowCount = useMemo(() => {
    if (total !== undefined) rowCountRef.current = total
    return rowCountRef.current
  }, [total])
  const invalidRange = dateRanges.some(
    ([key]) =>
      filters[`${key}_start`] &&
      filters[`${key}_end`] &&
      filters[`${key}_start`] > filters[`${key}_end`]
  )

  const applyFilters = () => {
    if (invalidRange) return
    // Preserve date-only end values so the API includes the entire UTC day.
    const next: Partial<GetUserCourseParams> = {}
    for (const [key] of dateRanges) {
      if (filters[`${key}_start`])
        next[`${key}_start`] = filters[`${key}_start`]
      if (filters[`${key}_end`]) next[`${key}_end`] = filters[`${key}_end`]
    }
    if (filters.status) next.status = filters.status
    if (selectedCourses.length)
      next.course = selectedCourses.map((course) => course.uuid)
    setAppliedFilters(next)
    setPaginationModel((previous) => ({ ...previous, page: 0 }))
    setExportError(false)
  }
  const clearFilters = () => {
    setFilters(emptyFilters)
    setAppliedFilters({})
    setSelectedGroups([])
    setSelectedCourses([])
    setPaginationModel({ page: 0, pageSize: 25 })
    setExportError(false)
  }
  const exportCsv = async () => {
    setExporting(true)
    setExportError(false)
    try {
      await exportUserCoursesAsCSV({
        ...appliedFilters,
        ...groupParams,
        org_id: orgId,
      })
    } catch {
      setExportError(true)
    } finally {
      setExporting(false)
    }
  }

  return (
    <Stack spacing={4}>
      <ReportPageHeader
        title={userCourseReportPage.pageTitle}
        description={userCourseReportPage.endpointDescription}
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportCsv}
            disabled={isFetching || exporting || !!error || !total}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        }
      />
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <GroupFilterSelect
              organizationId={orgId}
              value={selectedGroups}
              onChange={(groups) => {
                setSelectedGroups(groups)
                setPaginationModel((previous) => ({ ...previous, page: 0 }))
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignSelf: "flex-start",
              }}
            >
              <TextField
                select
                label="Status"
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                size="small"
                value={filters.status}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    status: event.target.value as Filters["status"],
                  }))
                }
                sx={{ width: 320 }}
              >
                <MenuItem value="">All statuses</MenuItem>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <CourseFilterSelect
                organizationId={orgId}
                value={selectedCourses}
                onChange={setSelectedCourses}
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
              {dateRanges.map(([key, label]) => (
                <DateRangeFilter
                  key={key}
                  label={label}
                  startDate={filters[`${key}_start`]}
                  endDate={filters[`${key}_end`]}
                  onStartDateChange={(value) =>
                    setFilters((previous) => ({
                      ...previous,
                      [`${key}_start`]: value,
                    }))
                  }
                  onEndDateChange={(value) =>
                    setFilters((previous) => ({
                      ...previous,
                      [`${key}_end`]: value,
                    }))
                  }
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
              <Button
                variant="contained"
                onClick={applyFilters}
                disabled={!!invalidRange}
              >
                Apply
              </Button>
              <Button variant="outlined" onClick={clearFilters}>
                Clear
              </Button>
            </Box>
            {invalidRange && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Each From date must be on or before its "To" date.
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
      {error && (
        <Alert severity="error">
          Unable to load course progress. Please check your filters and
          organization access, then try again.
        </Alert>
      )}
      {exportError && (
        <Alert severity="error">
          Unable to export course progress. Please try again.
        </Alert>
      )}
      <Card>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          rowCount={rowCount}
          loading={isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnFilter
          disableColumnMenu
          sx={{ "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0" } }}
        />
      </Card>
    </Stack>
  )
}

export default function UserCoursePage() {
  const orgId = useAppSelector(selectActiveOrgId)
  // Reset filters, pagination and retained row counts on organization changes.
  return <UserCourseReport key={orgId} orgId={orgId} />
}
